
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface FeedbackData {
  id: string;
  candidate_name: string;
  job_title: string;
  created_at: string;
  certified: boolean;
  recruiter_name?: string | null;
  feedback_type?: string | null;
  status?: string | null;
}

export function useFeedbacksFetcher() {
  const [feedbacks, setFeedbacks] = useState<FeedbackData[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, profile } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      fetchFeedbacks();
    }
  }, [user, profile]);

  const fetchFeedbacks = async () => {
    if (!user || !profile) {
      setFeedbacks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log("Récupération des feedbacks pour utilisateur:", user.email);
      
      // Utiliser la nouvelle fonction RPC qui retourne tous les feedbacks d'un utilisateur
      const { data, error } = await supabase.rpc('get_user_feedbacks', {
        p_user_id: user.id,
        p_user_email: user.email
      });
      
      if (error) {
        console.error("Erreur lors de l'appel à get_user_feedbacks:", error);
        throw error;
      }
      
      console.log(`Récupération réussie: ${data?.length || 0} feedbacks trouvés via RPC`);
      
      // Si la fonction RPC n'est pas disponible, on tente une approche de secours
      if (!data || data.length === 0) {
        console.log("Aucun feedback trouvé via RPC, tentative de récupération directe...");
        
        // Tentative de récupération directe par l'ID utilisateur et email
        const { data: directFeedbacks, error: directError } = await supabase
          .from('feedbacks')
          .select('*')
          .or(`candidate_user_id.eq.${user.id},user_id.eq.${user.id},candidate_email.ilike.${user.email?.toLowerCase()},recruiter_email.ilike.${user.email?.toLowerCase()}`)
          .order('created_at', { ascending: false });
          
        if (directError) {
          console.error("Erreur lors de la récupération directe:", directError);
          throw directError;
        }
        
        setFeedbacks(directFeedbacks as FeedbackData[] || []);
        console.log(`Récupération directe: ${directFeedbacks?.length || 0} feedbacks trouvés`);
      } else {
        setFeedbacks(data as FeedbackData[]);
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des feedbacks:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de charger les feedbacks",
        variant: "destructive",
      });
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  };

  return {
    feedbacks,
    loading,
    refetch: fetchFeedbacks
  };
}
